import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { ArrowRight, Check } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import type { PatternText } from '@/shared/patterns/text';
import { Button } from '@/shared/ui/button';

import { FlowPanel } from './flow-panel';

const DATA_STEP = 'dados';
const REVIEW_STEP = 'revisao';

export interface ReviewSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  /** O título na revisão ("Confirmar tratativa"); sem ele, o mesmo. */
  readonly reviewTitle?: ReactNode;
  readonly description?: ReactNode;
  /** Os nomes das duas etapas no mini-trilho; padrão "Dados · Revisão". */
  readonly stepTitles?: readonly [PatternText, PatternText] | undefined;
  /** Em revisão (controlado por quem abre: é quem guarda a escolha). */
  readonly reviewing: boolean;
  /** "Continuar" da primeira etapa (quem abre valida e passa `reviewing`). */
  readonly onReview: () => void;
  /** "Voltar" da revisão. */
  readonly onBack: () => void;
  /** `true` desliga o "Continuar"; um texto desliga e diz por quê, à vista. */
  readonly reviewDisabled?: boolean | string | undefined;
  readonly reviewLabel?: string | undefined;
  /** O verbo da ação (U24, U20.6): "Confirmar", "Gerar processo". */
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  /** A saída da primeira etapa; padrão "Fechar". */
  readonly dismissLabel?: string | undefined;
  /** Gravando: nada fecha nem volta. */
  readonly busy?: boolean | undefined;
  readonly size?: 'sm' | 'md' | undefined;
  readonly slot?: string | undefined;
  readonly children: ReactNode;
}

/**
 * Prévia → confirmação sem MFA (U53, discovery/13 família F): o painel de duas etapas ("Dados · Revisão"), "Continuar"
 * com o motivo à vista quando desligado e o verbo da ação na revisão. Nada é gravado antes do `onConfirm`.
 */
export function ReviewSheet({
  open,
  onOpenChange,
  title,
  reviewTitle,
  description,
  stepTitles,
  reviewing,
  onReview,
  onBack,
  reviewDisabled = false,
  reviewLabel,
  confirmLabel,
  onConfirm,
  dismissLabel,
  busy = false,
  size,
  slot,
  children,
}: ReviewSheetProps) {
  const { t } = useLingui();
  const reasonId = useId();
  const [dataTitle, reviewStepTitle] = stepTitles ?? [msg`Dados`, msg`Revisão`];
  const reason = typeof reviewDisabled === 'string' && reviewDisabled !== '' ? reviewDisabled : undefined;
  const off = reviewDisabled !== false && reviewDisabled !== '';
  return (
    <FlowPanel
      open={open}
      onOpenChange={onOpenChange}
      title={reviewing ? (reviewTitle ?? title) : title}
      description={description}
      steps={[
        { id: DATA_STEP, title: dataTitle },
        { id: REVIEW_STEP, title: reviewStepTitle },
      ]}
      current={reviewing ? REVIEW_STEP : DATA_STEP}
      onBack={reviewing ? onBack : undefined}
      dismissLabel={dismissLabel}
      busy={busy}
      size={size}
      slot={slot ?? 'review-sheet'}
      actions={
        reviewing ? (
          <Button type="button" loading={busy} onClick={onConfirm}>
            <Check aria-hidden />
            {confirmLabel}
          </Button>
        ) : (
          <>
            {reason ? (
              <p id={reasonId} className="self-center text-xs text-muted-foreground sm:max-w-56 sm:text-right">
                {reason}
              </p>
            ) : null}
            <Button type="button" disabled={off} aria-describedby={reason ? reasonId : undefined} onClick={onReview}>
              <ArrowRight aria-hidden />
              {reviewLabel ?? t`Continuar`}
            </Button>
          </>
        )
      }
    >
      {children}
    </FlowPanel>
  );
}
